import { useId, useMemo } from 'react';
import { channelByKey, formatValue } from '@ccs/core';
import { useChannelSeries } from '../hooks';
import { formatTime } from '../../../shared/format';

const WINDOW_MS = 5 * 60_000;

type Props = {
  datasetId: string;
  channelKey: string;
  cursor: string | null;
  playing: boolean;
  closable?: boolean;
  onClose: () => void;
};

const W = 1000;
const H = 150;
const PAD = 10;

export default function CurvePanel({
  datasetId,
  channelKey,
  cursor,
  playing,
  closable = true,
  onClose,
}: Props) {
  const channel = channelByKey.get(channelKey);
  const { points, loading, failed } = useChannelSeries(datasetId, channelKey, cursor);
  const clipId = useId();

  const cursorMs = cursor ? new Date(cursor).getTime() : null;
  const windowEnd = cursorMs !== null && Number.isFinite(cursorMs) ? cursorMs : null;
  const windowStart = windowEnd === null ? null : windowEnd - WINDOW_MS;

  const numeric = useMemo(
    () =>
      points
        .map((p) => ({ t: new Date(p.ts).getTime(), v: p.value }))
        .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v)),
    [points],
  );

  // 只画游标之前的读数：窗口右端始终是播放游标，左边固定回看 5 分钟
  const visible = useMemo(() => {
    if (windowStart === null || windowEnd === null) return [];
    return numeric.filter((p) => p.t >= windowStart && p.t <= windowEnd);
  }, [numeric, windowStart, windowEnd]);

  // 窗口左边界之外保留一个点，曲线才能贴着左边缘进入，而不是从半空开始
  const lead = useMemo(() => {
    if (windowStart === null) return null;
    for (let i = numeric.length - 1; i >= 0; i -= 1) {
      if (numeric[i].t < windowStart) return numeric[i];
    }
    return null;
  }, [numeric, windowStart]);

  const values = visible.map((p) => p.v);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const current = values.length ? values[values.length - 1] : null;

  // 上下留白，避免曲线贴着边缘；同时让平直数据落在中间
  const rawSpan = max - min;
  const padding = rawSpan > 0 ? rawSpan * 0.12 : Math.max(Math.abs(max) * 0.02, 0.5);
  const lo = min - padding;
  const hi = max + padding;
  const span = hi - lo || 1;

  const xAt = (t: number) =>
    windowStart === null ? 0 : PAD + ((t - windowStart) / WINDOW_MS) * (W - PAD * 2);
  const yAt = (v: number) => H - PAD - ((v - lo) / span) * (H - PAD * 2);

  const drawable = lead ? [lead, ...visible] : visible;
  const linePoints = drawable
    .map((p) => `${xAt(p.t).toFixed(1)},${yAt(p.v).toFixed(1)}`)
    .join(' ');
  const areaPoints = drawable.length
    ? `${xAt(drawable[0].t).toFixed(1)},${(H - PAD).toFixed(1)} ${linePoints} ${xAt(
        drawable[drawable.length - 1].t,
      ).toFixed(1)},${(H - PAD).toFixed(1)}`
    : '';

  const status = failed ? '同步失败' : loading ? '同步中…' : playing ? '跟随播放' : '已暂停';

  // 跳转后旧数据还没换窗时，不要误报“没有读数”
  const rangeStart = numeric.length ? numeric[0].t : null;
  const rangeEnd = numeric.length ? numeric[numeric.length - 1].t : null;
  const cursorCovered =
    rangeStart === null ||
    rangeEnd === null ||
    (windowEnd !== null && windowEnd >= rangeStart && windowEnd <= rangeEnd);

  return (
    <section className="panel curve curve-float">
      <div className="curve-head">
        <div>
          <h2>{channel?.name ?? channelKey}</h2>
          <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
            {channelKey} · {channel?.unit ?? '—'} · 显示最近 5 分钟
          </div>
        </div>
        <div className="spacer" />
        <span className="badge accent">
          当前 {current === null ? '—' : formatValue(current, channel)} {channel?.unit ?? ''}
        </span>
        <span className="badge">最小 {values.length ? formatValue(min, channel) : '—'}</span>
        <span className="badge">最大 {values.length ? formatValue(max, channel) : '—'}</span>
        {closable ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            关闭
          </button>
        ) : null}
      </div>

      {visible.length >= 2 ? (
        <>
          <svg className="curve-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
            <defs>
              <clipPath id={clipId}>
                <rect x={PAD} y={0} width={W - PAD * 2} height={H} />
              </clipPath>
              <linearGradient id={`${clipId}-fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0f766e" stopOpacity="0.14" />
                <stop offset="100%" stopColor="#0f766e" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((ratio) => (
              <line
                key={ratio}
                x1={PAD}
                x2={W - PAD}
                y1={PAD + ratio * (H - PAD * 2)}
                y2={PAD + ratio * (H - PAD * 2)}
                stroke="#e2e8f0"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <g clipPath={`url(#${clipId})`}>
              <polygon points={areaPoints} fill={`url(#${clipId}-fill)`} />
              <polyline
                points={linePoints}
                fill="none"
                stroke="#0f766e"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
            {/* 游标线：始终在右边缘，示意数据窗口的“现在” */}
            <line
              x1={W - PAD}
              x2={W - PAD}
              y1={PAD}
              y2={H - PAD}
              stroke="#0f766e"
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.45"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <div className="curve-axis">
            <span>{windowStart === null ? '--:--:--' : formatTime(new Date(windowStart).toISOString())}</span>
            <span className={playing && !loading && !failed ? 'curve-live' : undefined}>
              {status}
            </span>
            <span>{formatTime(cursor)}</span>
          </div>
        </>
      ) : (
        <div className="empty" style={{ padding: 24 }}>
          {failed
            ? '曲线数据加载失败'
            : loading || !cursorCovered
              ? '加载曲线…'
              : '当前窗口内没有足够的读数'}
        </div>
      )}
    </section>
  );
}